/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import SuspenseLoader from '../../components/SuspenseLoader/SuspenseLoader';
import Reset from '../../assets/icons/refresh.svg';
import './Sidebar.scss';
import RecentChanges from './RecentChanges';
import useFilterApi from '../../hooks/useFilterAPI';
import api, { apiWithRetry } from '../../utils/api';

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  onGo?: (filters: any) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onCollapse,
  onGo
}) => {

  /* ================= STATE ================= */
  // const yearOptions = ['2025', '2024', '2023'];
  // const customerOptions = ['CVS', 'Walmart', 'Target'];
  // const categoryOptions = ['Allergy', 'Cold', 'Flu'];
  // const brandOptions = ['Affrin', 'Zyrtec', 'Claritin'];
  const [summaryView, setSummaryView] = useState(false);

  const [year, setYear] = useState<string | null>(null);
  const [customer, setCustomer] = useState<number | null>(null);
  const [category, setCategory] = useState<number | null>(null);
  const [brand, setBrand] = useState<number | null>(null);

  const [yearOptions, setYearOptions] = useState<any[]>([]);
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [brandOptions, setBrandOptions] = useState<any[]>([]);
  const [auditExpanded, setAuditExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  const [activeButton, setActiveButton] =
    useState<'portfolio' | 'category'>('portfolio');

  const [filtersExpanded, setFiltersExpanded] = useState(true);

  /* ================= FETCH YEARS ================= */

  useEffect(() => {
    const fetchYears = async () => {
      try {
        setLoading(true)

        const res = await apiWithRetry(() =>
          api.get('/fec/filters/years')
        );

        if (res?.data?.success) {
          const years = res.data.data.map((y: any) =>
            y.year.toString()
          );

          setYearOptions(years);
          setYear(years[0]);
        }
      } catch (err) {
        console.error('Year API error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchYears();
  }, []);

  /* ================= FETCH CUSTOMERS ================= */

  useFilterApi(
    year
      ? `/fec/filters/customers?year=${year}`
      : null,
    setCustomerOptions,
    () => {
      setCustomer(null);
      setCategory(null);
      setBrand(null);
      setCategoryOptions([]);
      setBrandOptions([]);
    },
    setLoading
  );

  /* ================= FETCH CATEGORIES ================= */

  useFilterApi(
    year && customer
      ? `/fec/filters/categories?year=${year}&customerId=${customer}`
      : null,
    setCategoryOptions,
    () => {
      setCategory(null);
      setBrand(null);
      setBrandOptions([]);
    },
    setLoading
  );

  /* ================= FETCH BRANDS ================= */

  useFilterApi(
    year && customer && category
      ? `/fec/filters/brands?year=${year}&customerId=${customer}&categoryId=${category}`
      : null,
    setBrandOptions,
    () => {
      setBrand(null);
    },
    setLoading
  );

  /* ================= RESET ================= */

  const resetFilters = () => {
    setCustomer(null);
    setCategory(null);
    setBrand(null);
    setSummaryView(true);
    setActiveButton('portfolio');
  };

  return (
    <>
      {loading && <SuspenseLoader />}
      <div
        className={`custom-sidebar ${collapsed ? 'collapsed' : ''}`}
      >
        {/* COLLAPSE ARROW */}
        <div
          className={`sidebar-arrow ${collapsed ? 'collapsed' : ''}`}
          onClick={() => onCollapse?.(!collapsed)}
        >
          <span className="arrow arrow-1"></span>
          <span className="arrow arrow-2"></span>
        </div>

        <div className="sidebar-content">

          {/* Toggle */}
          <div className="view-toggle flex align-items-center justify-content-between">
            <span className={summaryView ? 'inactive' : ''}>Detailed</span>
            <div className="custom-toggle-wrapper">
              <InputSwitch
                checked={summaryView}
                onChange={(e) => setSummaryView(e.value!)}
              />
            </div>
            <span className={!summaryView ? 'inactive' : ''}>Summary</span>
          </div>

          {/* ================= FILTER SECTION ================= */}
          <div className="sidebar-section">

            <div
              className="section-header"
            >
              <span>Filters</span>
              <div className="flex gap-3">
                <img
                  src={Reset}   // your svg
                  alt="Reset"
                  className="section-header--icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetFilters();
                  }}
                />
                <i className={`pi ${filtersExpanded ? 'pi-chevron-up' : 'pi-chevron-down'} section-header--icon`} onClick={() => setFiltersExpanded(!filtersExpanded)} />
              </div>
            </div>

            {filtersExpanded && (
              <div className="filter-section">

                {/* Year */}
                <div className="filter-group">
                  <label>Select Year</label>
                  <Dropdown
                    value={year}
                    options={yearOptions}
                    onChange={(e) => setYear(e.value)}
                    placeholder="Year"
                    className="dropdown-custom"
                    appendTo={document.body}
                  />
                </div>

                {/* Customer */}
                <div className="filter-group">
                  <label>Customer</label>
                  <Dropdown
                    value={customer}
                    options={customerOptions}
                    onChange={(e) => setCustomer(e.value)}
                    placeholder="Customer"
                    appendTo={document.body}
                    disabled={!year}
                    optionLabel="name"
                    optionValue="id"
                  />
                </div>

                {/* Category */}
                <div className="filter-group">
                  <label>Category</label>
                  <Dropdown
                    value={category}
                    options={categoryOptions}
                    onChange={(e) => setCategory(e.value)}
                    placeholder="Category"
                    appendTo={document.body}
                    optionLabel="categoryName"
                    optionValue="categoryId"
                    disabled={!customer}
                  />
                </div>

                {/* Brand */}
                <div className="filter-group">
                  <label>Brand</label>
                  <Dropdown
                    value={brand}
                    options={brandOptions}
                    onChange={(e) => setBrand(e.value)}
                    placeholder="Brand"
                    appendTo={document.body}
                    optionLabel="brandName"
                    optionValue="brandId"
                    disabled={!category}
                  />
                </div>

                {summaryView && (
                  <div className="toggle-buttons">
                    <Button
                      label="Portfolio"
                      className={activeButton === 'portfolio' ? 'active-toggle' : ''}
                      onClick={() => setActiveButton('portfolio')}
                    />
                    <Button
                      label="Category"
                      className={activeButton === 'category' ? 'active-toggle' : ''}
                      onClick={() => setActiveButton('category')}
                    />
                  </div>
                )}

                <Button
                  label="Go"
                  className="go-button"
                  onClick={() =>
                    onGo?.({
                      year: year ? Number(year) : null,
                      customer,
                      category,
                      brand,
                      isSummaryView: summaryView
                    })
                  }
                />
              </div>
            )}
          </div>

          {/* ================= AUDIT / RECENT CHANGES ================= */}
          <div>
            <RecentChanges />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
